import axios from 'axios'
import { getApiUrl, API_ENDPOINTS } from '../config/api'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiUrl(''),
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post(API_ENDPOINTS.LOGIN, { email, password })
    return response.data
  },
  
  register: async (email: string, password: string, name?: string) => {
    const response = await api.post(API_ENDPOINTS.REGISTER, { email, password, name })
    return response.data
  },
  
  getProfile: async () => {
    const response = await api.get(API_ENDPOINTS.PROFILE)
    return response.data
  },
  
  updateProfile: async (data: any) => {
    const response = await api.put(API_ENDPOINTS.PROFILE, data)
    return response.data
  },
}

// Products API
export const productsAPI = {
  search: async (query: string, limit = 10, offset = 0) => {
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH, {
        params: { q: query, limit, offset }
      })
      return response.data || { products: [], total: 0 }
    } catch (error) {
      console.error('Search API error:', error)
      return { products: [], total: 0 }
    }
  },
  
  getProduct: async (id: string) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.PRODUCT}/${id}`)
      return response.data
    } catch (error) {
      console.error('Get product API error:', error)
      return null
    }
  },
  
  getPopular: async (limit = 10) => {
    try {
      const response = await api.get(API_ENDPOINTS.POPULAR, {
        params: { limit }
      })
      return response.data || { products: [], total: 0 }
    } catch (error) {
      console.error('Popular products API error:', error)
      return { products: [], total: 0 }
    }
  },
  
  addReview: async (productId: string, rating: number, comment?: string) => {
    const response = await api.post(`${API_ENDPOINTS.PRODUCT}/${productId}/reviews`, {
      rating,
      comment
    })
    return response.data
  },
}

// Submissions API
export const submissionsAPI = {
  create: async (data: any) => {
    const response = await api.post(API_ENDPOINTS.SUBMISSIONS, data)
    return response.data
  },
  
  getMySubmissions: async () => {
    const response = await api.get(API_ENDPOINTS.MY_SUBMISSIONS)
    return response.data
  },
  
  getStats: async () => {
    const response = await api.get(API_ENDPOINTS.SUBMISSION_STATS)
    return response.data
  },
  
  update: async (id: string, data: any) => {
    const response = await api.put(`${API_ENDPOINTS.SUBMISSIONS}/${id}`, data)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`${API_ENDPOINTS.SUBMISSIONS}/${id}`)
    return response.data
  },
}

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await api.get(API_ENDPOINTS.ADMIN_STATS)
    return response.data
  },
  
  getSubmissions: async (status?: string, limit = 20, offset = 0) => {
    const response = await api.get(API_ENDPOINTS.ADMIN_SUBMISSIONS, {
      params: { status, limit, offset }
    })
    return response.data
  },
  
  reviewSubmission: async (id: string, action: 'approve' | 'reject', adminNotes?: string) => {
    const response = await api.post(`${API_ENDPOINTS.ADMIN_SUBMISSIONS}/${id}/review`, {
      action,
      adminNotes
    })
    return response.data
  },
  
  getUsers: async (limit = 20, offset = 0) => {
    const response = await api.get(API_ENDPOINTS.ADMIN_USERS, {
      params: { limit, offset }
    })
    return response.data
  },
  
  updateUserRole: async (id: string, role: 'USER' | 'ADMIN') => {
    const response = await api.put(`${API_ENDPOINTS.ADMIN_USERS}/${id}/role`, { role })
    return response.data
  },
  
  getAlternativeRoutes: async (status?: string, limit = 20, offset = 0) => {
    const response = await api.get(API_ENDPOINTS.ADMIN_ROUTES, {
      params: { status, limit, offset }
    })
    return response.data
  },
  
  updateRouteStatus: async (id: string, status: string) => {
    const response = await api.put(`${API_ENDPOINTS.ADMIN_ROUTES}/${id}/status`, { status })
    return response.data
  },
  
  addCoupon: async (routeId: string, data: any) => {
    const response = await api.post(`${API_ENDPOINTS.ADMIN_ROUTES}/${routeId}/coupons`, data)
    return response.data
  },
}

export default api
